<template>
  <div class="rd-screen wh-inv">
    <report-tools :copy="rows.length > 0" :columns="false" @copy="copyTable">
      <ui-chips v-model="stockStatusFilter" :options="statusChips" :label="$t('status')" />

      <template #right>
        <v-menu offset-y left content-class="rd-vmenu">
          <template #activator="{ on, attrs }">
            <button type="button" class="ui-btn ui-btn--ok ui-btn--sm" data-action="order" v-bind="attrs" v-on="on">
              <v-icon size="16" color="#ffffff">mdi-cart-outline</v-icon>
              {{ $t("order") }}
              <v-icon size="16" color="#ffffff">mdi-chevron-down</v-icon>
            </button>
          </template>
          <ul class="rd-vmenu__list" role="menu">
            <li>
              <button type="button" role="menuitem" data-action="request" @click="orderGroceries(false)">
                <v-icon size="16">mdi-file-document-outline</v-icon>
                {{ $t("warehouse.request_quote") }}
              </button>
            </li>
            <li>
              <button type="button" role="menuitem" data-action="purchase" @click="orderGroceries(true)">
                <v-icon size="16">mdi-cart-arrow-down</v-icon>
                {{ $t("warehouse.purchase_order") }}
              </button>
            </li>
          </ul>
        </v-menu>
        <button type="button" class="ui-btn ui-btn--primary ui-btn--sm" data-action="entry" @click="openDialog('purchaseItemDialog')">
          <v-icon size="16" color="#ffffff">mdi-plus</v-icon>
          {{ $t("add_warehouse") }}
        </button>
        <button type="button" class="ui-btn ui-btn--sm" data-action="release" @click="openDialog('waybillDialog')">
          <v-icon size="16">mdi-truck-delivery-outline</v-icon>
          {{ $t("warehouse.release") }}
        </button>
        <button type="button" class="ui-btn ui-btn--sm" data-action="return" @click="openDialog('returnDialog')">
          <v-icon size="16">mdi-backup-restore</v-icon>
          {{ $t("warehouse.return") }}
        </button>
        <button type="button" class="ui-btn ui-btn--sm" data-action="writeoff" @click="openDialog('writeOffDialog')">
          <v-icon size="16" color="#b42318">mdi-trash-can-outline</v-icon>
          {{ $t("write_off") }}
        </button>
      </template>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-archive-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <template v-else>
      <div class="ui-card rd-card">
        <table class="ui-table rd-tbl wh-tbl">
          <thead>
            <tr>
              <th scope="col" :aria-sort="ariaSort('code')">
                <button type="button" @click="sortBy('code')">{{ $t("code") }} {{ sortArrow("code") }}</button>
              </th>
              <th scope="col" :aria-sort="ariaSort('name')">
                <button type="button" @click="sortBy('name')">{{ $t("warehouse.product") }} {{ sortArrow("name") }}</button>
              </th>
              <th scope="col" class="is-right" :aria-sort="ariaSort('quantity')">
                <button type="button" @click="sortBy('quantity')">{{ $t("quantity") }} {{ sortArrow("quantity") }}</button>
              </th>
              <th scope="col" class="is-right" :aria-sort="ariaSort('min')">
                <button type="button" @click="sortBy('min')">{{ $t("minimum_quantity") }} {{ sortArrow("min") }}</button>
              </th>
              <th scope="col">{{ $t("status") }}</th>
            </tr>
          </thead>
          <tbody v-for="group in visibleGroups" :key="group.key">
            <tr v-if="group.name" class="rd-tbl__group">
              <td colspan="5">
                {{ group.name }}
                <span class="rd-muted rd-num">· {{ group.total }}</span>
              </td>
            </tr>
            <tr v-for="row in group.rows" :key="row.key" :class="`wh-row--${row.status}`">
              <td class="rd-num rd-muted">{{ row.code || "—" }}</td>
              <td class="is-name" :title="row.name">{{ row.name }}</td>
              <td class="is-right rd-num">
                <b :class="{ 'is-zero': row.status === 'out' }">{{ qty(row.quantity) }}</b>
                <span class="wh-unit">{{ row.measure }}</span>
              </td>
              <td class="is-right rd-num" :class="{ 'is-zero': !row.min }">
                <template v-if="row.min">{{ qty(row.min) }}<span class="wh-unit">{{ row.measure }}</span></template>
                <template v-else>—</template>
              </td>
              <td>
                <span v-if="row.status === 'low'" class="ui-chip ui-chip--pill ui-chip--warn wh-pill">
                  <v-icon size="14">mdi-alert-outline</v-icon>
                  {{ $t("warehouse_shell.below_minimum") }}
                </span>
                <span v-else-if="row.status === 'out'" class="ui-chip ui-chip--pill ui-chip--bad wh-pill">
                  <v-icon size="14">mdi-minus-circle-outline</v-icon>
                  {{ $t("out_of_stock") }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="hiddenCount > 0" class="wh-more">
        <span class="rd-muted rd-num">{{ $t("warehouse_shell.shown_of", { shown: shownCount, total: rows.length }) }}</span>
        <button type="button" class="ui-btn ui-btn--sm" data-action="more" @click="showMore">
          {{ $t("warehouse_shell.show_more") }}
        </button>
      </div>
    </template>

    <warehouse-purchase-order-dialog v-model="purchaseOrderDialog" :edited-item="editedItem" />
    <warehouse-entry-grocery-dialog v-model="purchaseItemDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-waybill-dialog v-model="waybillDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-write-off-dialog v-model="writeOffDialog" :edited-item="editedItem" @success="triggerRefresh" />
    <warehouse-return-dialog v-model="returnDialog" :edited-item="editedItem" @success="triggerRefresh" />
  </div>
</template>

<script>
import WarehouseTabInventory from "@/components/warehouse/tabs/WarehouseTabInventory.vue";
import warehouseService from "@/services/warehouse";
import warehouseFormatMixin from "@/mixins/warehouse/warehouseFormatMixin";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";

const PAGE_SIZE = 100;
const CACHE_TTL = 5 * 60 * 1000;
const DOT = { in_stock: "#15803d", minimum: "#92400e", out_of_stock: "#b42318" };

/**
 * Stanje magacina na računaru: artikli sa količinom, minimumom i oznakom (ispod minimuma / nema na stanju), izbor
 * stanja sa brojačima, grupe po magacinu kad ih je više izabrano i radnje (naruči, dodaj, izdaj, povrat, otpis) u
 * alatima. Nasleđuje učitavanje, štampu, Excel i dijaloge iz starog taba; menjaju se prikaz i filtriranje.
 * Uvek se učitavaju i zalihe i svi artikli, pa brojači važe za sve izbore odjednom.
 */
export default {
  name: "WarehouseDesktopInventory",
  extends: WarehouseTabInventory,
  mixins: [hasTableSort, warehouseFormatMixin],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
    limit: PAGE_SIZE,
  }),
  computed: {
    multiWarehouse() {
      return (this.filters.warehouse || []).length > 1;
    },
    /** Stavke posle pretrage (stanje i brojači se računaju nad njima). */
    searchedItems() {
      return this.customSearch(this.translatedInventory, this.headers, this.filters.search);
    },
    allRows() {
      return this.searchedItems.map((item) => {
        const quantity = Number(item.quantity) || 0;
        const min = Number(item.grocery.min_quantity) || 0;
        return {
          key: item.id,
          item,
          code: item.code || "",
          name: item.grocery.name,
          measure: item.measure || "",
          quantity,
          min,
          warehouseId: item.warehouse.id,
          warehouseName: item.warehouse.name,
          status: quantity <= 0 ? "out" : min && quantity < min ? "low" : "ok",
        };
      });
    },
    counts() {
      const counts = { all: this.allRows.length, in_stock: 0, minimum: 0, out_of_stock: 0 };
      this.allRows.forEach((row) => {
        if (row.status === "out") {
          counts.out_of_stock += 1;
        } else {
          counts.in_stock += 1;
          if (row.status === "low") {
            counts.minimum += 1;
          }
        }
      });
      return counts;
    },
    statusChips() {
      return [
        { value: "all", label: this.$t("all_items"), count: this.counts.all },
        { value: "in_stock", label: this.$t("in_stock"), count: this.counts.in_stock, dot: DOT.in_stock },
        { value: "minimum", label: this.$t("warehouse_shell.below_minimum"), count: this.counts.minimum, dot: DOT.minimum },
        { value: "out_of_stock", label: this.$t("out_of_stock"), count: this.counts.out_of_stock, dot: DOT.out_of_stock },
      ];
    },
    /** Stavke izabranog stanja, poređane (unutar magacina kad ih je više). */
    rows() {
      const wanted = {
        all: () => true,
        in_stock: (row) => row.status !== "out",
        minimum: (row) => row.status === "low",
        out_of_stock: (row) => row.status === "out",
      }[this.stockStatusFilter];
      const sorted = this.sortRows(this.allRows.filter(wanted || (() => true)));
      if (!this.multiWarehouse) {
        return sorted;
      }
      const order = this.filters.warehouse.map((warehouse) => warehouse.id);
      return [...sorted].sort((a, b) => order.indexOf(a.warehouseId) - order.indexOf(b.warehouseId));
    },
    visibleRows() {
      return this.rows.slice(0, this.limit);
    },
    shownCount() {
      return this.visibleRows.length;
    },
    hiddenCount() {
      return this.rows.length - this.visibleRows.length;
    },
    /** [{ key, name, total, rows }]: jedna grupa bez naziva za jedan magacin. */
    visibleGroups() {
      if (!this.multiWarehouse) {
        return [{ key: "all", name: "", total: this.rows.length, rows: this.visibleRows }];
      }
      const totals = {};
      this.rows.forEach((row) => {
        totals[row.warehouseId] = (totals[row.warehouseId] || 0) + 1;
      });
      const groups = [];
      this.visibleRows.forEach((row) => {
        let group = groups[groups.length - 1];
        if (!group || group.key !== row.warehouseId) {
          group = { key: row.warehouseId, name: row.warehouseName, total: totals[row.warehouseId], rows: [] };
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups;
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("no_inventory");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("no_inventory_yet");
    },
    /** Štampa i Excel prate prikazani redosled i izabrano stanje. */
    exportData() {
      return this.rows.map((row) => row.item);
    },
    shownKpis() {
      if (this.loading) {
        return [];
      }
      return [
        { key: "items", label: this.$t("warehouse_shell.kpi_items"), value: this.rows.length, tone: "inverse" },
        {
          key: "low",
          label: this.$t("warehouse_shell.below_minimum"),
          value: this.counts.minimum,
          tone: this.counts.minimum ? "accent" : "",
        },
      ];
    },
  },
  watch: {
    shownKpis: {
      handler(kpis) {
        this.$emit("kpis", kpis);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
    // novi izbor, pretraga ili magacin: lista se ponovo prikazuje od početka
    stockStatusFilter() {
      this.limit = PAGE_SIZE;
    },
    "filters.search"() {
      this.limit = PAGE_SIZE;
    },
    "filters.warehouse"() {
      this.limit = PAGE_SIZE;
    },
  },
  methods: {
    qty(value) {
      return this.formatQuantity(value);
    },
    showMore() {
      this.limit += PAGE_SIZE;
    },
    /** Kod, naziv i broj kolona su isti kao ranije; ovde se uvek učitavaju i zalihe i svi artikli (stari tab je to radio samo za neke izbore). */
    async fetchInventory(forceRefresh = false) {
      this.loading = true;
      try {
        const now = Date.now();
        if (
          !forceRefresh &&
          this.cachedInventory &&
          this.cachedGroceries &&
          this.lastFetchTime &&
          now - this.lastFetchTime < CACHE_TTL
        ) {
          this.inventory = this.buildInventoryFromData(this.cachedInventory, this.cachedGroceries);
          return;
        }
        const [status, groceries] = await Promise.all([
          warehouseService.getWarehouseStatus({
            warehouse_ids: this.filters.warehouse.map((warehouse) => warehouse.id).join(","),
            date: this.filters.dates.from,
          }),
          warehouseService.getGroceriesWithWarehouse(),
        ]);
        this.cachedInventory = status.data;
        this.cachedGroceries = groceries.data;
        this.lastFetchTime = now;
        this.inventory = this.buildInventoryFromData(status.data, groceries.data);
      } catch (error) {
        console.error("Error loading inventory:", error);
        this.showErrorSnackbar(this.$t("error_loading_inventory"));
      } finally {
        this.loading = false;
      }
    },
    sortBy(key) {
      this.sort = {
        key,
        dir: this.sort.key === key ? -this.sort.dir : key === "name" || key === "code" ? 1 : -1,
      };
    },
    async copyTable() {
      const headers = [
        ...(this.multiWarehouse ? [this.$t("stock")] : []),
        this.$t("code"),
        this.$t("warehouse.product"),
        this.$t("quantity"),
        this.$t("unit_of_measurment"),
        this.$t("minimum_quantity"),
      ];
      const rows = this.rows.map((row) => [
        ...(this.multiWarehouse ? [row.warehouseName] : []),
        row.code,
        row.name,
        this.qty(row.quantity),
        row.measure,
        row.min ? this.qty(row.min) : "",
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
